import { css, FlattenSimpleInterpolation } from "styled-components";

/* CSS rules that should apply when the app is running outside a regular
 * browser tab — Capacitor's WebView, an installed PWA, or a fullscreen
 * browser launch.
 *
 * Coverage:
 * - Android Capacitor reports `display-mode: fullscreen` (via
 *   WindowCompat.setDecorFitsSystemWindows = false in MainActivity).
 * - Installed PWAs report `display-mode: standalone`.
 * - iOS Capacitor's WKWebView reports `display-mode: browser` — there's
 *   no iOS equivalent of the Android decor-fits flag. So the media query
 *   alone misses iOS. We additionally match `html[data-capacitor-native]`,
 *   which index.html sets synchronously at script-tag time when the
 *   Capacitor bridge is present. Without this, layout rules tied to
 *   env(safe-area-inset-*) would silently drop on iOS — e.g. the
 *   BoxAndCartTray cart strip falls back to its web-mobile `top:
 *   header.mobileHeight` and leaves a white gap between the notch
 *   backdrop (painted by TopInsetSpacer) and the cart pills.
 *
 * Use this for layout / positioning concerns tied to env(safe-area-
 * inset-*) — those values resolve to non-zero only in standalone-style
 * contexts anyway, so the rules are inert in a regular browser tab.
 *
 * Don't use it for Capacitor-specific *features* that don't exist in a
 * PWA (the bottom native tab bar, the toast offset clearing it,
 * hardware-back wiring, status-bar plugin calls). Those should stay
 * gated on `isCapacitorNative()` / `Capacitor.isNativePlatform()`.
 *
 * Uses `&&` (not `&`): in styled-components v5, `&` resolves to the
 * component's static (displayName) class — shared across every prop
 * variant — so a styled component with a dynamic interpolation that
 * embeds standalone() would emit one stylesheet rule per variant, all
 * keyed on the same selector. The latest rule then wins for every
 * instance regardless of which variant class is currently applied,
 * which manifested as e.g. HolderInner staying at its open-state
 * height after the React state had switched back to closed. `&&`
 * doubles the per-variant dynamic class (`.bkrZIw.bkrZIw`) so each
 * variant generates a distinct selector and the rules no longer
 * collide.
 *
 * Usage:
 *   const Foo = styled.div`
 *     ${standalone(css`
 *       padding-top: env(safe-area-inset-top);
 *     `)}
 *   `;
 */
export const standalone = (rules: FlattenSimpleInterpolation) => css`
  @media (display-mode: standalone), (display-mode: fullscreen) {
    && {
      ${rules}
    }
  }
  html[data-capacitor-native="true"] && {
    ${rules}
  }
`;
